import { NgModule } from '@angular/core';
import { RouterModule, Routes, UrlMatchResult, UrlMatcher, UrlSegment } from '@angular/router';
import { AuthGuard } from './_theme/core/users/Auth/_services/auth.guard';
import { PublicStatusDomainGuard } from './_theme/core/users/Auth/_services/public-status-domain.guard';
import { StatusEntityRedirectComponent } from './modules/status-page/status-main-page/redirects/status-entity-redirect.component';
import { StatusVerifyRedirectComponent } from './modules/status-page/status-main-page/redirects/status-verify-redirect.component';
import { StatusMainPageManageSubscriptionComponent } from './modules/status-page/status-main-page/manage-subscription/status-main-page-manage-subscription.component';

import { StatusMainPageModule } from './modules/status-page/status-main-page/status-main-page.module';

const LEGACY_STATUS_LINK_RESERVED_SEGMENTS = new Set([
  'org',
  'error',
  'errors',
  'status-main-page',
  'expired-installation',
  'alert',
  'incident',
  'dashboard',
  'users',
  'groups-schedules',
  'integrations',
  'workflows',
  'administration',
  'escalation-policies',
  'response-plays',
  'status-page',
  'call-routing',
  'reports',
  'alerts',
  'incidents',
  'business-services',
  'configuration',
  'aiops',
  'user-management',
  'account-settings',
  'subscription',
  'billing',
  'verify',
  'unsubscribe',
  'manage',
  'subscriptions',
  'login',
  'internal',
  'i',
  'm'
]);

const legacyStatusPublicRouteMatcher: UrlMatcher = (segments: UrlSegment[]): UrlMatchResult | null => {
  if (typeof window === 'undefined' || !window.location?.hostname) {
    return null;
  }

  const host = String(window.location.hostname || '').toLowerCase().trim();
  const isAppHost = host === 'localhost' || host.endsWith('.localhost') || host.startsWith('app.');
  if (!isAppHost || segments.length !== 2) {
    return null;
  }

  const org = String(segments[0]?.path || '').trim().toLowerCase();
  const slug = String(segments[1]?.path || '').trim().toLowerCase();
  if (!org || !slug) {
    return null;
  }

  if (LEGACY_STATUS_LINK_RESERVED_SEGMENTS.has(org) || LEGACY_STATUS_LINK_RESERVED_SEGMENTS.has(slug)) {
    return null;
  }

  return {
    consumed: segments,
    posParams: {
      org: segments[0],
      slug: segments[1]
    }
  };
};

export const routes: Routes = [
  {
    path: '',
    canMatch: [PublicStatusDomainGuard],
    loadChildren: () => Promise.resolve(StatusMainPageModule),
  },
  {
    path: 'org',
    loadChildren: () =>
      import('./modules/auth/auth.module').then((m) => m.AuthModule),
  },
  {
    path: 'error',
    loadChildren: () =>
      import('./modules/errors/errors.module').then((m) => m.ErrorsModule),
  },

  {
    path: ':subdomain/status-main-page',
    loadChildren: () => Promise.resolve(StatusMainPageModule)
  },
  {
    path: ':org/internal/:slug',
    pathMatch: 'full',
    redirectTo: ':org/status-main-page/internal/:slug'
  },
  {
    path: ':org/:slug/verify',
    component: StatusVerifyRedirectComponent,
    data: { subscriptionAction: 'verify' }
  },
  {
    path: ':org/:slug/unsubscribe',
    component: StatusVerifyRedirectComponent,
    data: { subscriptionAction: 'unsubscribe' }
  },
  {
    path: ':slug/verify',
    component: StatusVerifyRedirectComponent,
    data: { subscriptionAction: 'verify' }
  },
  {
    path: ':slug/unsubscribe',
    component: StatusVerifyRedirectComponent,
    data: { subscriptionAction: 'unsubscribe' }
  },
  {
    path: ':slug/manage',
    component: StatusMainPageManageSubscriptionComponent,
    data: { manageLinkSupported: true }
  },
  {
    path: ':account/:slug/manage',
    component: StatusMainPageManageSubscriptionComponent,
    data: { manageLinkSupported: true }
  },
  {
    path: ':slug/subscriptions/manage',
    component: StatusMainPageManageSubscriptionComponent,
    data: { manageLinkSupported: false }
  },
  {
    path: ':account/:slug/subscriptions/manage',
    component: StatusMainPageManageSubscriptionComponent,
    data: { manageLinkSupported: false }
  },
  {
    path: 'i/:statusId/:incidentId',
    component: StatusEntityRedirectComponent,
    data: { redirectSource: 'short-link', entityType: 'incident' }
  },
  {
    path: 'm/:statusId/:maintenanceId',
    component: StatusEntityRedirectComponent,
    data: { redirectSource: 'short-link', entityType: 'maintenance' }
  },
  {
    matcher: legacyStatusPublicRouteMatcher,
    redirectTo: ':org/status-main-page/:slug'
  },
  {
    path: '',
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('./pages/layout.module').then((m) => m.LayoutModule),
  },
  {
    path: ':subdomain/expired-installation',
    loadChildren: () =>
      import('./modules/expired-installation/expired-installation.module').then((m) => m.ExpiredInstallationModule),
  },
  {
    path: ':subdomain',
    loadChildren: () =>
      import('./modules/mailaction/mailaction.module').then((m) => m.MailactionModule),
  },
  { path: '', redirectTo: 'org/account', pathMatch: 'full' }
];

@NgModule({
  imports: [RouterModule.forRoot(routes, {})],
  exports: [RouterModule],
})
export class AppRoutingModule {}
